import { useCallback, useEffect, useState } from "react";
import { Icon } from "../components/Icon.js";
import { coloringApi, type PagePromptInfo } from "./api.js";
import { useColoring } from "./ColoringContext.js";
import { AutoField, useAction } from "./fields.js";
import { STATUS_LABEL, type ColoringCharacter, type ColoringPage, type PageInput } from "./types.js";
import { PageGenerationControls, PagePreview } from "./PageGeneration.js";

/**
 * The detailed page editor: metadata on the left, a large preview in the middle,
 * prompt + generation controls on the right.
 */
export function PageEditor({
  page,
  index,
  total,
  characters,
  onClose,
  onNavigate
}: {
  page: ColoringPage;
  index: number;
  total: number;
  characters: ColoringCharacter[];
  onClose: () => void;
  onNavigate: (delta: number) => void;
}) {
  const { projectId, reload } = useColoring();
  const { run } = useAction();

  const save = useCallback(
    async (patch: PageInput) => {
      await run("save", async () => {
        await coloringApi.updatePage(projectId, page.id, patch);
        await reload();
      });
    },
    [projectId, page.id, reload, run]
  );

  // Keyboard: Esc closes, arrows (when not typing) move between pages.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      const typing = el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT");
      if (e.key === "Escape" && !typing) onClose();
      else if (!typing && e.key === "ArrowLeft") onNavigate(-1);
      else if (!typing && e.key === "ArrowRight") onNavigate(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onNavigate]);

  const toggleCharacter = (id: string) => {
    const has = page.characterIds.includes(id);
    void save({ characterIds: has ? page.characterIds.filter((c) => c !== id) : [...page.characterIds, id] });
  };

  return (
    <div className="page-editor-overlay" role="dialog" aria-label={`Page ${index + 1} editor`}>
      <header className="page-editor-head">
        <button className="btn btn-sm btn-ghost" onClick={onClose}>
          <Icon name="x" size={14} /> Close
        </button>
        <div className="row gap-2">
          <button className="btn btn-icon btn-ghost btn-sm" disabled={index <= 0} onClick={() => onNavigate(-1)} title="Previous page">
            <Icon name="chevronRight" size={15} style={{ transform: "rotate(180deg)" }} />
          </button>
          <span className="strong">Page {index + 1} <span className="faint">of {total}</span></span>
          <button className="btn btn-icon btn-ghost btn-sm" disabled={index >= total - 1} onClick={() => onNavigate(1)} title="Next page">
            <Icon name="chevronRight" size={15} />
          </button>
        </div>
        <span className={`status-badge status-${page.status}`}>{STATUS_LABEL[page.status]}</span>
      </header>

      <div className="page-editor-body">
        <aside className="page-editor-left">
          <AutoField label="Page title / internal name" value={page.title} onSave={(v) => save({ title: v })} />
          <AutoField label="Scene description" rows={5} value={page.scene} onSave={(v) => save({ scene: v })} placeholder="What is happening on this page?" />
          <div className="field">
            <label className="label">Characters on this page</label>
            {characters.length === 0 ? (
              <span className="faint" style={{ fontSize: 12 }}>No recurring characters in this book.</span>
            ) : (
              <div className="choice-row">
                {characters.map((c) => (
                  <button key={c.id} type="button" className={`choice ${page.characterIds.includes(c.id) ? "on" : ""}`} onClick={() => toggleCharacter(c.id)}>
                    {c.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          <AutoField label="Location" value={page.location} onSave={(v) => save({ location: v })} />
          <AutoField label="Objects / props" rows={2} value={page.props} onSave={(v) => save({ props: v })} />
          <AutoField label="Composition notes" rows={2} value={page.composition} onSave={(v) => save({ composition: v })} />
        </aside>

        <section className="page-editor-center">
          <PagePreview page={page} />
        </section>

        <aside className="page-editor-right">
          <PageGenerationControls page={page} />
          <PromptEditor page={page} save={save} />
        </aside>
      </div>
    </div>
  );
}

/** Shows the exact prompt that will be sent; lets the user pin their own. */
function PromptEditor({ page, save }: { page: ColoringPage; save: (patch: PageInput) => Promise<void> }) {
  const { projectId } = useColoring();
  const [info, setInfo] = useState<PagePromptInfo | null>(null);
  const [prompt, setPrompt] = useState("");
  const [negative, setNegative] = useState("");
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const next = await coloringApi.pagePrompt(projectId, page.id);
      setInfo(next);
      setPrompt(next.effective.prompt);
      setNegative(next.effective.negativePrompt);
    } catch {
      setInfo(null);
    }
  }, [projectId, page.id]);

  // Reload whenever the page's inputs change (scene, characters, custom prompt…).
  useEffect(() => {
    void load();
  }, [load, page.title, page.scene, page.location, page.props, page.composition, page.characterIds.join(","), page.prompt, page.negativePrompt]);

  const dirty = !!info && (prompt !== info.effective.prompt || negative !== info.effective.negativePrompt);

  return (
    <div className="gen-section">
      <button className="gen-section-head" onClick={() => setOpen((o) => !o)}>
        <Icon name={open ? "chevronDown" : "chevronRight"} size={14} />
        <span>Prompt {info?.custom && <span className="chip chip-accent" style={{ marginLeft: 6 }}>Custom</span>}</span>
      </button>
      {open && (
        <div className="gen-section-body">
          <p className="faint" style={{ fontSize: 11.5, marginTop: 0 }}>
            Built from your Style Bible, characters and this scene. Edit it to pin a custom prompt for this page only.
          </p>
          <label className="label">Prompt</label>
          <textarea className="textarea prompt-text" rows={12} value={prompt} onChange={(e) => setPrompt(e.target.value)} />
          <label className="label" style={{ marginTop: 10 }}>Negative prompt</label>
          <textarea className="textarea" rows={3} value={negative} onChange={(e) => setNegative(e.target.value)} />
          <div className="row gap-2" style={{ marginTop: 10 }}>
            <button className="btn btn-sm btn-primary" disabled={!dirty} onClick={() => void save({ prompt, negativePrompt: negative })}>
              Save custom prompt
            </button>
            <button className="btn btn-sm btn-ghost" disabled={!info?.custom} onClick={() => void save({ prompt: null, negativePrompt: null })}>
              Reset to auto
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
